import React, { createContext, useContext, useState, ReactNode } from 'react';
import SubscriptionRequiredDialog from '../components/dialogs/SubscriptionRequiredDialog';

interface SubscriptionDialogContextType {
  showSubscriptionDialog: (subscriptionType?: 'autoSearch' | 'autoNego' | 'mcp' | 'general') => void;
  openTenantSettings: (tab?: number) => void;
}

const SubscriptionDialogContext = createContext<SubscriptionDialogContextType | undefined>(undefined);

export const useSubscriptionDialog = () => {
  const context = useContext(SubscriptionDialogContext);
  if (!context) {
    throw new Error('useSubscriptionDialog must be used within SubscriptionDialogProvider');
  }
  return context;
};

interface SubscriptionDialogProviderProps {
  children: ReactNode;
  onOpenTenantSettings: (tab?: number) => void;
}

export const SubscriptionDialogProvider: React.FC<SubscriptionDialogProviderProps> = ({
  children,
  onOpenTenantSettings,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState<'autoSearch' | 'autoNego' | 'mcp' | 'general'>('general');

  const showSubscriptionDialog = (type: 'autoSearch' | 'autoNego' | 'mcp' | 'general' = 'general') => {
    setSubscriptionType(type);
    setIsDialogOpen(true);
  };

  const handleOpenSettings = () => {
    setIsDialogOpen(false);
    // Tab 11 is the Subscription tab in TenantSettingsModal
    onOpenTenantSettings(11);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
  };

  return (
    <SubscriptionDialogContext.Provider
      value={{
        showSubscriptionDialog,
        openTenantSettings: onOpenTenantSettings,
      }}
    >
      {children}
      <SubscriptionRequiredDialog
        open={isDialogOpen}
        onClose={handleClose}
        onOpenSettings={handleOpenSettings}
        subscriptionType={subscriptionType}
      />
    </SubscriptionDialogContext.Provider>
  );
};
